/**
 * Shared primitives for the calendar views (Day / Week / Month / Agenda).
 *
 * - Time grid constants (HOUR_PX, DAY_START_H, DAY_END_H, HOURS)
 * - Active-status set used by conflict detection
 * - Date helpers (isoDate, getMondayOf, addDays, startOfMonth, monthDays)
 * - Label helpers (DOW_ES_SHORT/LONG, MONTHS_ES, fmtTime, fmtDateLong)
 * - Position helpers (eventStyle, overrideStyle, bookingTone)
 */

export const DOW_ES_SHORT = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'];
export const DOW_ES_LONG = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
export const MONTHS_ES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
export const MONTHS_ES_SHORT = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];

export const HOUR_PX = 56;
export const DAY_START_H = 8;
export const DAY_END_H = 21;
export const HOURS = Array.from({ length: DAY_END_H - DAY_START_H + 1 }, (_, i) => DAY_START_H + i);

export const ACTIVE_BOOKING_STATUSES = new Set([
  'pending',
  'confirmed',
  'checked_in',
  'pending_clinic_approval',
]);

export function isoDate(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(d.getDate() + days);
  return x;
}

export function getMondayOf(d: Date): Date {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  const mon = new Date(d);
  mon.setDate(d.getDate() + diff);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

/** First Monday of the grid containing the given month (may belong to prev month). */
export function getMonthGridStart(d: Date): Date {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return getMondayOf(first);
}

export function fmtTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function fmtDateLong(d: Date): string {
  return `${DOW_ES_LONG[(d.getDay() + 6) % 7] ?? ''} ${String(d.getDate())} de ${MONTHS_ES[d.getMonth()] ?? ''} de ${String(d.getFullYear())}`;
}

export function fmtDateShort(iso: string): string {
  const [, m, dd] = iso.split('-').map(Number);
  return `${String(dd)} ${MONTHS_ES_SHORT[m - 1] ?? ''}`;
}

export function fmtLocalTime(hhmm: string, date: string): string {
  return `${date}T${hhmm}:00.000Z`;
}

export function eventStyle(startTime: string, endTime: string): React.CSSProperties {
  const s = new Date(startTime);
  const e = new Date(endTime);
  const startMins = (s.getHours() - DAY_START_H) * 60 + s.getMinutes();
  const durationMins = (e.getTime() - s.getTime()) / 60000;
  const top = startMins * (HOUR_PX / 60);
  const height = Math.max(durationMins * (HOUR_PX / 60) - 3, 20);
  return { top: `${String(top)}px`, height: `${String(height)}px` };
}

/**
 * The grid is rendered in the admin browser's local time (admin is Spanish-
 * only by design, used from Madrid — see `project-admin-i18n-by-design`).
 * `eventStyle` placed bookings via `getHours()/getMinutes()` (local) but the
 * historical `overrideStyle` mixed in `getUTCHours()/getUTCMinutes()`, so a
 * 09:00 Madrid override landed at hour 7 (CEST) or 8 (CET) on the grid.
 * Both helpers now use local methods for visual consistency.
 */
export function overrideStyle(startTime: string, endTime: string, dayIso: string): React.CSSProperties | null {
  // Day boundaries in local time (browser TZ) — matches `eventStyle`'s coords.
  const [year, month, day] = dayIso.split('-').map(Number);
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) return null;
  const dayStart = new Date(year, month - 1, day, 0, 0, 0, 0);
  const dayEnd   = new Date(year, month - 1, day, 23, 59, 59, 999);
  const ovStart  = new Date(startTime);
  const ovEnd    = new Date(endTime);
  const clampStart = ovStart < dayStart ? dayStart : ovStart;
  const clampEnd   = ovEnd > dayEnd     ? dayEnd   : ovEnd;
  if (clampStart >= clampEnd) return null;

  const startMins = (clampStart.getHours() - DAY_START_H) * 60 + clampStart.getMinutes();
  const durationMins = (clampEnd.getTime() - clampStart.getTime()) / 60000;
  if (startMins < 0 && durationMins <= 0) return null;
  const top    = Math.max(startMins, 0) * (HOUR_PX / 60);
  const height = Math.max(durationMins * (HOUR_PX / 60), 20);
  return { top: `${String(top)}px`, height: `${String(height)}px` };
}

export function bookingTone(status: string): string | undefined {
  if (status === 'pending') return 'warn';
  if (status === 'pending_clinic_approval') return 'warn';
  if (status === 'checked_in') return 'info';
  if (status === 'cancelled') return 'neutral';
  if (status === 'completed') return 'neutral';
  if (status === 'noshow') return 'neutral';
  return undefined;
}

export interface BlockForm {
  date: string;
  startHour: string;
  endHour: string;
  reason: string;
}

export const DEFAULT_BLOCK_FORM: BlockForm = {
  date: isoDate(new Date()),
  startHour: '09:00',
  endHour: '18:00',
  reason: '',
};
